import React, { useState, useEffect } from 'react';

import ManageClaimCodes from './ManageClaimCodes';
import PrizeEditor from './PrizeEditor';
import {
  Trophy,
  Plus,
  Trash2,
  Pencil,
  X,
  BarChart3,
  AlertTriangle,
  Settings
} from 'lucide-react';

const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

const CLAIM_CODE_MODE_OPTIONS = [
  { value: 'inherit', label: 'Prize default' },
  { value: 'static', label: 'Shared code' },
  { value: 'list', label: 'CSV list' }
];

const generateAllocationId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `alloc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

// Allocation ids that the server has already stored for a prize (needed before managing a CSV list).
const getSavedAllocationIds = (prize) => {
  try {
    const data = typeof prize.allocations === 'string' ? JSON.parse(prize.allocations) : prize.allocations;
    const list = Array.isArray(data) ? data : (data?.allocations || []);
    return new Set(list.map(item => item?.allocation_id).filter(Boolean));
  } catch (err) {
    return new Set();
  }
};

const InstantWinConfig = ({
  campaignId,
  enabled = false,
  onEnabledChange,
  distribution = 'low_to_high',
  onDistributionChange,
  prizes = [],
  onPrizesChange,
  formFields = [],
  api,
  stats = null,
  config = {},
  onConfigChange,
  setSuccessMessage,
  setShowSuccess,
  setErrorMessage,
  setShowError
}) => {
  const [showAddPrizeModal, setShowAddPrizeModal] = useState(false);
  const [savingPrize, setSavingPrize] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingPrizeId, setDeletingPrizeId] = useState(null);
  const [expandedClaimCodesPrizeId, setExpandedClaimCodesPrizeId] = useState(null);
  const [expandedEditPrizeId, setExpandedEditPrizeId] = useState(null);
  const [expandedAllocationCodesKey, setExpandedAllocationCodesKey] = useState(null);
  
  const [newPrize, setNewPrize] = useState({
    prize_name: '',
    prize_value: '',
    total_quantity: '',
    claim_code_prefix: '',
    range_start: 1,
    range_end: '',
    logo_url: '',
    claim_code_strategy: 'range'
  });

  const [prizeAllocations, setPrizeAllocations] = useState({});
  const [openProductDropdowns, setOpenProductDropdowns] = useState({});

  // Load prize allocations from existing prizes
  useEffect(() => {
    const allocations = {};
    prizes.forEach(prize => {
      if (prize.allocations) {
        const allocData = typeof prize.allocations === 'string' 
          ? JSON.parse(prize.allocations) 
          : prize.allocations;
        allocations[`prize_${prize.id}_allocations`] = allocData.allocations || [];
      }
    });
    setPrizeAllocations(allocations);
  }, [prizes]);
  
  // Auto-calculate range start based on existing prizes
  useEffect(() => {
    if (showAddPrizeModal && newPrize.claim_code_prefix) {
      // Find the highest range_end among existing prizes with the same prefix
      const samePrefix = prizes.filter(p => p.claim_code_prefix === newPrize.claim_code_prefix);
      if (samePrefix.length > 0) {
        const highestEnd = Math.max(...samePrefix.map(p => p.range_end || 0));
        if (highestEnd > 0 && !newPrize.range_start) {
          setNewPrize(prev => ({
            ...prev,
            range_start: highestEnd + 1
          }));
        }
      } else if (!newPrize.range_start) {
        // First prize with this prefix, start at 1
        setNewPrize(prev => ({
          ...prev,
          range_start: 1
        }));
      }
    }
  }, [showAddPrizeModal, newPrize.claim_code_prefix, prizes]);
  
  // Auto-calculate range end based on quantity
  useEffect(() => {
    if (newPrize.total_quantity && newPrize.range_start) {
      const calculatedEnd = parseInt(newPrize.range_start) + parseInt(newPrize.total_quantity) - 1;
      setNewPrize(prev => ({
        ...prev,
        range_end: calculatedEnd
      }));
    }
  }, [newPrize.total_quantity, newPrize.range_start]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside any dropdown container
      const dropdownContainer = event.target.closest('[data-dropdown-container]');
      const dropdownTrigger = event.target.closest('[data-dropdown-trigger]');
      
      // If clicking outside both container and trigger, close all dropdowns
      if (!dropdownContainer && !dropdownTrigger) {
        setOpenProductDropdowns({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAddPrize = async (e) => {
    e.preventDefault();
    setSavingPrize(true);
    
    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/instant-win/prizes`, newPrize);
      onPrizesChange([...prizes, response.data.prize]);
      
      setNewPrize({
        prize_name: '',
        prize_value: '',
        total_quantity: '',
        claim_code_prefix: '',
        range_start: 1,
        range_end: '',
        logo_url: '',
        claim_code_strategy: 'range'
      });
      setShowAddPrizeModal(false);
      setSuccessMessage('Prize added successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to add prize:', err);
      setErrorMessage('Failed to add prize: ' + (err.response?.data?.error || err.message));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    } finally {
      setSavingPrize(false);
    }
  };

  const updatePrizeInList = (updatedPrize) => {
    if (!updatedPrize || !updatedPrize.id) return;
    const nextPrizes = prizes.map((prize) =>
      prize.id === updatedPrize.id ? { ...prize, ...updatedPrize } : prize
    );
    onPrizesChange(nextPrizes);
  };

  const handleDeletePrize = async () => {
    try {
      await api.delete(`/admin/campaigns/${campaignId}/instant-win/prizes/${deletingPrizeId}`);
      onPrizesChange(prizes.filter(p => p.id !== deletingPrizeId));
      setExpandedClaimCodesPrizeId((current) => (current === deletingPrizeId ? null : current));
      setExpandedEditPrizeId((current) => (current === deletingPrizeId ? null : current));
      
      setShowDeleteConfirm(false);
      setDeletingPrizeId(null);
      setSuccessMessage('Prize deleted successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to delete prize:', err);
      setErrorMessage('Failed to delete prize: ' + (err.response?.data?.error || err.message));
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  };

  const getEntryFields = () => {
    return formFields.filter(field => 
      field.field_name.toLowerCase().includes('entr') ||
      field.field_name.toLowerCase() === 'entries'
    );
  };

  const getProductFields = () => {
    return formFields.filter(field => 
      field.field_name.toLowerCase().includes('product')
    );
  };

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="bg-gray-50 p-6 rounded-xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Instant Win Status</h4>
            <p className="text-sm text-gray-600">Enable instant win prizes for eligible entries</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={async (e) => {
                const newEnabled = e.target.checked;
                onEnabledChange(newEnabled);
                
                // Save to backend
                try {
                  await api.patch(`/admin/campaigns/${campaignId}/instant-win`, { 
                    enabled: newEnabled 
                  });
                  setSuccessMessage(newEnabled ? 'Instant Win enabled!' : 'Instant Win disabled!');
                  setShowSuccess(true);
                  setTimeout(() => setShowSuccess(false), 3000);
                } catch (err) {
                  console.error('Failed to toggle instant win:', err);
                  setErrorMessage('Failed to save instant win status');
                  setShowError(true);
                  setTimeout(() => setShowError(false), 3000);
                  // Revert the change
                  onEnabledChange(!newEnabled);
                }
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-teal-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
        
        {enabled && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              <strong>Note:</strong> Make sure your dynamic form includes fields for entry method and product type to determine eligibility.
            </p>
          </div>
        )}
      </div>

      {/* Distribution Mode */}
      {enabled && (
        <div className="bg-gray-50 p-6 rounded-xl">
          <h4 className="text-lg font-medium text-gray-900">Prize Distribution</h4>
          <p className="text-sm text-gray-600 mb-3">Choose how winning prizes are picked from the eligible allocations</p>
          <div className="space-y-2">
            {[
              { value: 'low_to_high', label: 'Low to high', help: 'Lowest-value prize goes out first; higher-value prizes are awarded once the cheaper ones run out.' },
              { value: 'random', label: 'Random across all allocations', help: 'Any eligible allocation can win, in no particular order. Allocations with more stock left are more likely to be picked.' }
            ].map(option => (
              <label key={option.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="instant-win-distribution"
                  className="mt-1"
                  checked={distribution === option.value}
                  onChange={async () => {
                    const previous = distribution;
                    onDistributionChange(option.value);
                    try {
                      await api.patch(`/admin/campaigns/${campaignId}/instant-win`, { distribution: option.value });
                      setSuccessMessage('Prize distribution updated!');
                      setShowSuccess(true);
                      setTimeout(() => setShowSuccess(false), 3000);
                    } catch (err) {
                      console.error('Failed to save distribution mode:', err);
                      setErrorMessage('Failed to save prize distribution');
                      setShowError(true);
                      setTimeout(() => setShowError(false), 3000);
                      onDistributionChange(previous);
                    }
                  }}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                  <span className="block text-sm text-gray-600">{option.help}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Prize Pool Management */}
      {enabled && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900">Prize Pool</h4>
            <button
              onClick={() => setShowAddPrizeModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600"
            >
              <Plus className="w-5 h-5" />
              <span>Add Prize</span>
            </button>
          </div>
          
          {/* Claim Code Range Summary */}
          {prizes.some(p => p.claim_code_prefix) && (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <h5 className="text-sm font-medium text-blue-900 mb-2">Claim Code Ranges</h5>
              <div className="space-y-1 text-sm">
                {prizes
                  .filter(p => p.claim_code_prefix)
                  .sort((a, b) => (a.claim_code_prefix + String(a.range_start).padStart(5, '0')).localeCompare(b.claim_code_prefix + String(b.range_start).padStart(5, '0')))
                  .map(prize => (
                    <div key={prize.id} className="text-blue-700">
                      <span className="font-medium">{prize.prize_name}:</span> {prize.claim_code_prefix}{String(prize.range_start).padStart(5, '0')} - {prize.claim_code_prefix}{String(prize.range_end).padStart(5, '0')}
                    </div>
                  ))}
              </div>
            </div>
          )}
          
          {prizes.some(p => p.claim_code_strategy === 'list') && (
            <div className="mb-4 p-4 bg-indigo-50 rounded-lg">
              <h5 className="text-sm font-medium text-indigo-900 mb-2">Voucher Pools</h5>
              <div className="space-y-1 text-sm">
                {prizes
                  .filter(p => p.claim_code_strategy === 'list')
                  .map(prize => (
                    <div key={prize.id} className="text-indigo-700 flex justify-between items-center">
                      <span className="font-medium">{prize.prize_name}</span>
                      <span className="ml-2 text-xs font-mono">
                        {prize.remaining_quantity} / {prize.total_quantity} vouchers available
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
          
          {prizes.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Trophy className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">No prizes configured yet.</p>
              <p className="text-sm text-gray-500">Add prizes to enable instant win functionality.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {prizes.map((prize) => (
                <div key={prize.id} className="border border-gray-200 rounded-xl p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-4 flex-1">
                      {prize.logo_url && (
                        <div className="w-20 h-20 flex-shrink-0 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            src={`${ASSET_BASE_URL}${prize.logo_url}`}
                            alt={`${prize.prize_name} logo`}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                      <h5 className="text-lg font-medium text-gray-900">{prize.prize_name}</h5>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            prize.claim_code_strategy === 'list'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-sky-100 text-sky-700'
                          }`}
                        >
                          {prize.claim_code_strategy === 'list' ? 'Voucher list' : 'Range'}
                        </span>
                        {prize.claim_code_strategy === 'list' ? (
                          <span className="text-xs text-gray-500">
                            {prize.remaining_quantity} of {prize.total_quantity} vouchers available
                          </span>
                        ) : (
                          prize.claim_code_prefix &&
                          Number.isFinite(Number(prize.range_start)) &&
                          Number.isFinite(Number(prize.range_end)) ? (
                            <span className="text-xs font-mono text-gray-500">
                              {prize.claim_code_prefix}
                              {String(Number(prize.range_start)).padStart(5, '0')} – {prize.claim_code_prefix}
                              {String(Number(prize.range_end)).padStart(5, '0')}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500">Randomized codes</span>
                          )
                        )}
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Value:</span>
                          <span className="ml-2 font-medium">${prize.prize_value}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total:</span>
                          <span className="ml-2 font-medium">{prize.total_quantity}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Remaining:</span>
                          <span className="ml-2 font-medium text-green-600">{prize.remaining_quantity}</span>
                        </div>
                      </div>
                      
                      {/* Progress Bar */}
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-teal-600 h-2 rounded-full transition-all"
                            style={{ width: `${(prize.remaining_quantity / prize.total_quantity) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {Math.round((prize.remaining_quantity / prize.total_quantity) * 100)}% remaining
                        </p>
                      </div>

                      {/* Dynamic Quantity Breakdown */}
                      {prize.total_quantity > 0 && (
                        <div className="mt-4 bg-gray-50 rounded-lg p-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Quantity Breakdown:</p>
                          <div className="space-y-2">
                            {(() => {
                              const allocKey = `prize_${prize.id}_allocations`;
                              const allocations = prizeAllocations[allocKey] || [];
                              
                              // Get entry fields and product fields
                              const entryFields = formFields.filter(field => 
                                field.field_name.toLowerCase().startsWith('entries') || 
                                field.field_name.toLowerCase().startsWith('entry') ||
                                field.field_name.toLowerCase() === 'entries'
                              );
                              const productFields = formFields.filter(field => 
                                field.field_name.toLowerCase().startsWith('product_')
                              );
                              
                              return allocations.map((allocation, index) => {
                                return (
                                  <div key={allocation.allocation_id || index}>
                                  <div className="grid grid-cols-12 gap-2 text-xs items-center">
                                    <input
                                      type="text"
                                      value={allocation.name}
                                      onChange={(e) => {
                                        const newAllocs = [...allocations];
                                        newAllocs[index].name = e.target.value;
                                        setPrizeAllocations({
                                          ...prizeAllocations,
                                          [allocKey]: newAllocs
                                        });
                                      }}
                                      className="col-span-3 px-2 py-1 border border-gray-300 rounded text-gray-600"
                                      placeholder="Allocation name"
                                    />
                                    
                                    {/* Entry field dropdown */}
                                    <select
                                      value={`${allocation.entry_field || ''}:${allocation.entry_value || ''}`}
                                      onChange={(e) => {
                                        const parts = e.target.value.split(':');
                                        const field = parts[0] || '';
                                        const value = parts.slice(1).join(':').trim(); // Handle values with colons
                                        const newAllocs = [...allocations];
                                        newAllocs[index].entry_field = field;
                                        newAllocs[index].entry_value = value;
                                        setPrizeAllocations({
                                          ...prizeAllocations,
                                          [allocKey]: newAllocs
                                        });
                                      }}
                                      className="col-span-3 px-2 py-2 border border-gray-300 rounded text-gray-600 text-xs"
                                    >
                                      <option value="">Select Entry Method</option>
                                      {entryFields.map(field => {
                                        // For simple number fields like entries, just use the field itself
                                        if (field.field_type === 'number' || field.field_type === 'text') {
                                          return (
                                            <option key={field.field_name} value={`${field.field_name}:`}>
                                              {field.field_label}
                                            </option>
                                          );
                                        }
                                        
                                        // For fields with options
                                        let options = [];
                                        if (field.field_options) {
                                          const parsed = typeof field.field_options === 'string' 
                                            ? JSON.parse(field.field_options) 
                                            : field.field_options;
                                          options = Array.isArray(parsed) ? parsed : (parsed.options || []);
                                        }
                                        return options.map(option => (
                                          <option key={`${field.field_name}:${option}`} value={`${field.field_name}:${option}`}>
                                            {field.field_label}: {option}
                                          </option>
                                        ));
                                      })}
                                    </select>
                                    
                                    {/* Product field dropdown - custom multi-select */}
                                    <div className="col-span-3 relative">
                                      <button
                                        type="button"
                                        data-dropdown-trigger
                                        onClick={() => {
                                          const dropdownKey = `${prize.id}_${index}`;
                                          setOpenProductDropdowns({
                                            ...openProductDropdowns,
                                            [dropdownKey]: !openProductDropdowns[dropdownKey]
                                          });
                                        }}
                                        className="w-full px-2 py-2 border border-gray-300 rounded text-gray-600 text-xs text-left flex justify-between items-center"
                                      >
                                        <span>
                                          {allocation.product_values && allocation.product_values.length > 0
                                            ? `${allocation.product_values.length} products selected`
                                            : 'Select Products'}
                                        </span>
                                        <BarChart3 className="w-4 h-4" />
                                      </button>
                                      
                                      {openProductDropdowns[`${prize.id}_${index}`] && (
                                        <div data-dropdown-container className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                                          {productFields.map(field => {
                                            // For simple fields like product names
                                            if (field.field_type === 'text' || field.field_type === 'hidden') {
                                              const value = `${field.field_name}:`;
                                              const isSelected = allocation.product_values?.includes(value);
                                              return (
                                                <label key={field.field_name} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected || false}
                                                    onChange={() => {
                                                      const newAllocs = [...allocations];
                                                      if (!newAllocs[index].product_values) {
                                                        newAllocs[index].product_values = [];
                                                      }
                                                      if (isSelected) {
                                                        newAllocs[index].product_values = newAllocs[index].product_values.filter(v => v !== value);
                                                      } else {
                                                        newAllocs[index].product_values.push(value);
                                                      }
                                                      setPrizeAllocations({
                                                        ...prizeAllocations,
                                                        [allocKey]: newAllocs
                                                      });
                                                    }}
                                                    className="mr-2"
                                                  />
                                                  <span className="text-xs">{field.field_label}</span>
                                                </label>
                                              );
                                            }
                                            
                                            // For fields with options
                                            let options = [];
                                            if (field.field_options) {
                                              const parsed = typeof field.field_options === 'string' 
                                                ? JSON.parse(field.field_options) 
                                                : field.field_options;
                                              options = Array.isArray(parsed) ? parsed : (parsed.options || []);
                                            }
                                            return options.map(option => {
                                              const value = `${field.field_name}:${option}`;
                                              // More flexible matching to handle minor string differences
                                              const isSelected = allocation.product_values?.some(storedValue => {
                                                // Extract field name and option from stored value
                                                const [storedFieldName, ...storedOptionParts] = storedValue.split(':');
                                                const storedOption = storedOptionParts.join(':');
                                                
                                                // Check if field names match
                                                if (storedFieldName !== field.field_name) return false;
                                                
                                                // Compare normalized versions (remove extra spaces, case insensitive)
                                                const normalize = (str) => str.toLowerCase().replace(/\s+/g, ' ').trim();
                                                return normalize(storedOption) === normalize(option);
                                              });
                                              return (
                                                <label key={value} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer">
                                                  <input
                                                    type="checkbox"
                                                    checked={isSelected || false}
                                                    onChange={() => {
                                                      const newAllocs = [...allocations];
                                                      if (!newAllocs[index].product_values) {
                                                        newAllocs[index].product_values = [];
                                                      }
                                                      if (isSelected) {
                                                        // Use flexible matching to find and remove the stored value
                                                        newAllocs[index].product_values = newAllocs[index].product_values.filter(storedValue => {
                                                          const [storedFieldName, ...storedOptionParts] = storedValue.split(':');
                                                          const storedOption = storedOptionParts.join(':');
                                                          if (storedFieldName !== field.field_name) return true;
                                                          const normalize = (str) => str.toLowerCase().replace(/\s+/g, ' ').trim();
                                                          return normalize(storedOption) !== normalize(option);
                                                        });
                                                      } else {
                                                        newAllocs[index].product_values.push(value);
                                                      }
                                                      setPrizeAllocations({
                                                        ...prizeAllocations,
                                                        [allocKey]: newAllocs
                                                      });
                                                    }}
                                                    className="mr-2"
                                                  />
                                                  <span className="text-xs">{field.field_label}: {option}</span>
                                                </label>
                                              );
                                            });
                                          })}
                                        </div>
                                      )}
                                    </div>
                                    
                                    <input
                                      type="number"
                                      value={allocation.quantity || ''}
                                      onChange={(e) => {
                                        const newAllocs = [...allocations];
                                        const value = e.target.value;
                                        // Allow empty string or valid numbers
                                        newAllocs[index].quantity = value === '' ? '' : parseInt(value) || 0;
                                        setPrizeAllocations({
                                          ...prizeAllocations,
                                          [allocKey]: newAllocs
                                        });
                                      }}
                                      className="col-span-2 px-2 py-2 border border-gray-300 rounded text-gray-900 font-medium"
                                      min="0"
                                      max={prize.total_quantity}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newAllocs = allocations.filter((_, i) => i !== index);
                                        setPrizeAllocations({
                                          ...prizeAllocations,
                                          [allocKey]: newAllocs
                                        });
                                      }}
                                      className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Per-row claim code */}
                                  {(() => {
                                    const mode = allocation.claim_code_mode || 'inherit';
                                    const rowKey = `${prize.id}:${allocation.allocation_id}`;
                                    const isSaved = !!allocation.allocation_id && getSavedAllocationIds(prize).has(allocation.allocation_id);
                                    const updateRow = (changes) => {
                                      const newAllocs = [...allocations];
                                      newAllocs[index] = { ...newAllocs[index], ...changes };
                                      setPrizeAllocations({
                                        ...prizeAllocations,
                                        [allocKey]: newAllocs
                                      });
                                    };
                                    return (
                                      <div className="mt-1 mb-2 flex flex-wrap items-center gap-2 text-xs pl-1">
                                        <span className="text-gray-500">Claim code:</span>
                                        <select
                                          value={mode}
                                          onChange={(e) => {
                                            const next = e.target.value;
                                            updateRow({
                                              claim_code_mode: next,
                                              allocation_id: allocation.allocation_id || generateAllocationId()
                                            });
                                            if (next !== 'list' && expandedAllocationCodesKey === rowKey) {
                                              setExpandedAllocationCodesKey(null);
                                            }
                                          }}
                                          className="px-2 py-1 border border-gray-300 rounded text-gray-600"
                                        >
                                          {CLAIM_CODE_MODE_OPTIONS.map(option => (
                                            <option key={option.value} value={option.value}>{option.label}</option>
                                          ))}
                                        </select>
                                        {mode === 'static' && (
                                          <input
                                            type="text"
                                            value={allocation.static_claim_code || ''}
                                            onChange={(e) => updateRow({ static_claim_code: e.target.value })}
                                            className="px-2 py-1 border border-gray-300 rounded font-mono text-gray-700 w-56"
                                            placeholder="Code given to every winner"
                                          />
                                        )}
                                        {mode === 'list' && (
                                          <button
                                            type="button"
                                            disabled={!isSaved}
                                            title={isSaved ? 'Upload and manage this row\'s codes' : 'Save allocations first'}
                                            onClick={() =>
                                              setExpandedAllocationCodesKey(current => (current === rowKey ? null : rowKey))
                                            }
                                            className="inline-flex items-center gap-1 px-2 py-1 border border-teal-200 text-teal-700 rounded hover:bg-teal-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            <Settings className="w-3.5 h-3.5" />
                                            {expandedAllocationCodesKey === rowKey ? 'Hide codes' : 'Manage codes'}
                                          </button>
                                        )}
                                        {mode === 'list' && !isSaved && (
                                          <span className="text-amber-600">Save allocations to upload codes</span>
                                        )}
                                      </div>
                                    );
                                  })()}

                                  {expandedAllocationCodesKey === `${prize.id}:${allocation.allocation_id}` && (
                                    <div className="mb-3 rounded-xl border border-teal-100 bg-teal-50/70 p-4">
                                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-700 mb-3">
                                        Claim Code Configuration · {allocation.name || 'Purchase type'}
                                      </p>
                                      <ManageClaimCodes
                                        variant="embedded"
                                        showHeader={false}
                                        open
                                        campaignId={campaignId}
                                        prize={prize}
                                        allocation={allocation}
                                        api={api}
                                        onPrizeUpdated={updatePrizeInList}
                                        setSuccessMessage={setSuccessMessage}
                                        setShowSuccess={setShowSuccess}
                                        setErrorMessage={setErrorMessage}
                                        setShowError={setShowError}
                                        className="bg-white rounded-2xl shadow-sm"
                                      />
                                    </div>
                                  )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                          
                          {/* Action buttons */}
                          <div className="flex gap-2 mt-3">
                            <button
                              type="button"
                              onClick={() => {
                                const allocKey = `prize_${prize.id}_allocations`;
                                const currentAllocs = prizeAllocations[allocKey] || [];
                                setPrizeAllocations({
                                  ...prizeAllocations,
                                  [allocKey]: [...currentAllocs, { 
                                    allocation_id: generateAllocationId(),
                                    claim_code_mode: 'inherit',
                                    name: '', 
                                    entry_field: '',
                                    entry_value: '',
                                    product_field: '',
                                    product_value: '',
                                    product_values: [],
                                    quantity: 0 
                                  }]
                                });
                              }}
                              className="text-xs text-teal-600 hover:text-teal-700"
                            >
                              + Add Purchase Type
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  const allocKey = `prize_${prize.id}_allocations`;
                                  const allocations = prizeAllocations[allocKey] || [];
                                  
                                  // Send the prize-wide claim code settings too: the update endpoint rewrites
                                  // them, so leaving them out would reset the prize default.
                                  const { data } = await api.put(`/admin/campaigns/${campaignId}/instant-win/prizes/${prize.id}`, {
                                    prize_name: prize.prize_name,
                                    prize_value: parseFloat(prize.prize_value),
                                    total_quantity: parseInt(prize.total_quantity),
                                    allocations: { allocations },
                                    claim_code_strategy: prize.claim_code_strategy,
                                    claim_code_prefix: prize.claim_code_prefix,
                                    range_start: prize.range_start,
                                    range_end: prize.range_end
                                  });
                                  
                                  setSuccessMessage('Prize allocations saved successfully!');
                                  setShowSuccess(true);
                                  setTimeout(() => setShowSuccess(false), 3000);
                                  // Use the server's copy so allocation ids it assigned are picked up.
                                  updatePrizeInList(data?.prize || { ...prize, allocations: { allocations } });
                                } catch (err) {
                                  console.error('Failed to save allocations:', err);
                                  setErrorMessage('Failed to save allocations: ' + (err.response?.data?.error || err.message));
                                  setShowError(true);
                                  setTimeout(() => setShowError(false), 3000);
                                }
                              }}
                              className="px-3 py-1 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 font-medium"
                            >
                              Save Allocations
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    </div>
                    
                    {/* Prize Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setExpandedEditPrizeId(null);
                          setExpandedClaimCodesPrizeId((current) =>
                            current === prize.id ? null : prize.id
                          );
                        }}
                        className={`p-2 rounded-full transition ${
                          expandedClaimCodesPrizeId === prize.id
                            ? 'text-teal-600 bg-teal-50'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Manage Claim Codes"
                        type="button"
                      >
                        <Settings className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setExpandedClaimCodesPrizeId(null);
                          setExpandedEditPrizeId((current) =>
                            current === prize.id ? null : prize.id
                          );
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600"
                        title="Edit Prize"
                        type="button"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingPrizeId(prize.id);
                          setShowDeleteConfirm(true);
                        }}
                        className="p-2 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                  </div>
                </div>

                {expandedClaimCodesPrizeId === prize.id && (
                  <div className="mt-6">
                    <div className="rounded-3xl border-2 border-teal-100 bg-teal-50/70 p-6 shadow-inner space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-teal-700">
                            Claim Code Configuration
                          </p>
                          <p className="text-sm text-teal-900 mt-2">
                            Adjust voucher strategy and manage uploaded codes for this prize.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedClaimCodesPrizeId(null)}
                          className="text-teal-600 hover:text-teal-700 text-sm font-medium"
                        >
                          Hide
                        </button>
                      </div>
                      <ManageClaimCodes
                        variant="embedded"
                        showHeader={false}
                        open
                        campaignId={campaignId}
                        prize={prize}
                        api={api}
                        onPrizeUpdated={updatePrizeInList}
                        setSuccessMessage={setSuccessMessage}
                        setShowSuccess={setShowSuccess}
                        setErrorMessage={setErrorMessage}
                        setShowError={setShowError}
                        className="bg-white rounded-2xl shadow-sm"
                      />
                    </div>
                  </div>
                )}

                {expandedEditPrizeId === prize.id && (
                  <div className="mt-6">
                    <div className="rounded-3xl border-2 border-indigo-200 bg-indigo-50/60 p-6 shadow-inner space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.35em] text-indigo-700">
                            Edit Prize Details
                          </p>
                          <p className="text-sm text-indigo-900 mt-2">
                            Update the name, quantity, value, and branding for this reward tier.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedEditPrizeId(null)}
                          className="text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                        >
                          Hide
                        </button>
                      </div>
                      <PrizeEditor
                        prize={prize}
                        campaignId={campaignId}
                        api={api}
                        onPrizeUpdated={updatePrizeInList}
                        setSuccessMessage={setSuccessMessage}
                        setShowSuccess={setShowSuccess}
                        setErrorMessage={setErrorMessage}
                        setShowError={setShowError}
                        onCancel={() => setExpandedEditPrizeId(null)}
                        className="bg-white rounded-2xl shadow-sm"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {/* Add Prize Modal */}
      {showAddPrizeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Add New Prize</h3>
              <button
                onClick={() => setShowAddPrizeModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleAddPrize} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prize Name*
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newPrize.prize_name}
                  onChange={(e) => setNewPrize({ ...newPrize, prize_name: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prize Value ($)*
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newPrize.prize_value}
                  onChange={(e) => setNewPrize({ ...newPrize, prize_value: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total Quantity*
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                  value={newPrize.total_quantity}
                  onChange={(e) => setNewPrize({ ...newPrize, total_quantity: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddPrizeModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPrize}
                  className="px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
                >
                  {savingPrize ? 'Adding...' : 'Add Prize'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Prize?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this prize? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingPrizeId(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePrize}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Prize
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InstantWinConfig;
